import React, { useState, useEffect } from 'react'
import { Modal, Button, Form, Row, Col, Tabs, Tab } from 'react-bootstrap'
import { EditorBlock, BlockConfigData } from './types'
import { MediaManagerModal } from '../MediaManagerModal'

interface BlockConfigurationModalProps {
    show: boolean
    onHide: () => void
    block: EditorBlock | null
    initialData?: BlockConfigData
    onConfirm: (data: BlockConfigData) => void
}

export const BlockConfigurationModal: React.FC<BlockConfigurationModalProps> = ({
    show,
    onHide,
    block,
    initialData,
    onConfirm,
}) => {
    const [data, setData] = useState<BlockConfigData>({})
    const [showMediaManager, setShowMediaManager] = useState(false)
    const [mediaTarget, setMediaTarget] = useState<'imageUrl' | 'logoUrl' | null>(null)
    const [activeTab, setActiveTab] = useState<string>('content')

    // Reset data when block changes
    useEffect(() => {
        if (show && block) {
            // Start with initialData if provided, otherwise use defaults
            const baseData = initialData || {}
            const mergedData: BlockConfigData = {
                ...baseData,
                align: baseData.align || 'center',
                padding: baseData.padding || '20px',
                blockSpacing: baseData.blockSpacing || '20px',
                border: baseData.border,
                buttonColor: baseData.buttonColor || '#007bff',
                buttonText: baseData.buttonText || 'Click Here',
                title: baseData.title || 'Heading',
                subtitle: baseData.subtitle || 'Subtitle text goes here',
                content: baseData.content || '',
                backgroundColor: baseData.backgroundColor || '#f8f9fa',
                paddingTop: baseData.paddingTop || '20px',
                paddingBottom: baseData.paddingBottom || '20px',
                paddingLeft: baseData.paddingLeft || '20px',
                paddingRight: baseData.paddingRight || '20px',
                buttonPaddingVertical: baseData.buttonPaddingVertical || '12px',
                buttonPaddingHorizontal: baseData.buttonPaddingHorizontal || '25px',
                // Preserve column content
                col1Content: baseData.col1Content,
                col2Content: baseData.col2Content,
                col3Content: baseData.col3Content,
                // Preserve column widths
                col1Width: baseData.col1Width,
                col2Width: baseData.col2Width,
                col3Width: baseData.col3Width,
                // Preserve column styling
                col1BackgroundColor: baseData.col1BackgroundColor,
                col2BackgroundColor: baseData.col2BackgroundColor,
                col3BackgroundColor: baseData.col3BackgroundColor,
                col1Padding: baseData.col1Padding,
                col2Padding: baseData.col2Padding,
                col3Padding: baseData.col3Padding,
                col1Border: baseData.col1Border,
                col2Border: baseData.col2Border,
                col3Border: baseData.col3Border,
            }
            setData(mergedData)
        }
    }, [show, block, initialData])

    const handleChange = (field: keyof BlockConfigData, value: any) => {
        setData((prev) => ({ ...prev, [field]: value }))
    }

    const handleMediaSelect = (url: string) => {
        if (mediaTarget === 'imageUrl') {
            handleChange('imageUrl', url)
        } else if (mediaTarget === 'logoUrl') {
            handleChange('logoUrl', url)
        }
        setShowMediaManager(false)
        setMediaTarget(null)
    }

    const openMediaFor = (target: 'imageUrl' | 'logoUrl') => {
        setMediaTarget(target)
        setShowMediaManager(true)
    }

    const renderFields = () => {
        if (!block) return null

        switch (block.id) {
            case 'hero':
                return (
                    <>
                        <Form.Group className="mb-3">
                            <Form.Label>Hero Image</Form.Label>
                            <div className="d-flex gap-2">
                                <Form.Control type="text" value={data.imageUrl || ''} onChange={e => handleChange('imageUrl', e.target.value)} placeholder="https://..." />
                                <Button variant="outline-secondary" onClick={() => openMediaFor('imageUrl')}>Select</Button>
                            </div>
                        </Form.Group>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Image Width</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={data.imageWidth || ''}
                                        onChange={e => handleChange('imageWidth', e.target.value)}
                                        placeholder="e.g., 600px, 100%"
                                    />
                                    <Form.Text>Leave empty for full width</Form.Text>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Image Height</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={data.imageHeight || ''}
                                        onChange={e => handleChange('imageHeight', e.target.value)}
                                        placeholder="e.g., 300px, auto"
                                    />
                                    <Form.Text>Leave empty for auto</Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Form.Group className="mb-3">
                            <Form.Label>Title</Form.Label>
                            <Form.Control type="text" value={data.title || ''} onChange={e => handleChange('title', e.target.value)} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Subtitle</Form.Label>
                            <Form.Control as="textarea" rows={2} value={data.subtitle || ''} onChange={e => handleChange('subtitle', e.target.value)} />
                        </Form.Group>
                        <Row>
                            <Col>
                                <Form.Group className="mb-3">
                                    <Form.Label>Button Text</Form.Label>
                                    <Form.Control type="text" value={data.buttonText || ''} onChange={e => handleChange('buttonText', e.target.value)} />
                                </Form.Group>
                            </Col>
                            <Col>
                                <Form.Group className="mb-3">
                                    <Form.Label>Button URL</Form.Label>
                                    <Form.Control type="text" value={data.buttonUrl || ''} onChange={e => handleChange('buttonUrl', e.target.value)} />
                                </Form.Group>
                            </Col>
                        </Row>
                    </>
                )

            case 'image_text':
                return (
                    <>
                        <Form.Group className="mb-3">
                            <Form.Label>Image</Form.Label>
                            <div className="d-flex gap-2">
                                <Form.Control type="text" value={data.imageUrl || ''} onChange={e => handleChange('imageUrl', e.target.value)} placeholder="https://..." />
                                <Button variant="outline-secondary" onClick={() => openMediaFor('imageUrl')}>Select</Button>
                            </div>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Heading</Form.Label>
                            <Form.Control type="text" value={data.title || ''} onChange={e => handleChange('title', e.target.value)} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Content</Form.Label>
                            <Form.Control as="textarea" rows={3} value={data.content || ''} onChange={e => handleChange('content', e.target.value)} />
                        </Form.Group>
                        <Row>
                            <Col>
                                <Form.Group className="mb-3">
                                    <Form.Label>CTA Text (Optional)</Form.Label>
                                    <Form.Control type="text" value={data.buttonText || ''} onChange={e => handleChange('buttonText', e.target.value)} />
                                </Form.Group>
                            </Col>
                            <Col>
                                <Form.Group className="mb-3">
                                    <Form.Label>CTA URL</Form.Label>
                                    <Form.Control type="text" value={data.buttonUrl || ''} onChange={e => handleChange('buttonUrl', e.target.value)} />
                                </Form.Group>
                            </Col>
                        </Row>
                    </>
                )

            case 'button':
                return (
                    <>
                        <Form.Group className="mb-3">
                            <Form.Label>Lead Title (Optional)</Form.Label>
                            <Form.Control
                                type="text"
                                value={data.title || ''}
                                onChange={e => handleChange('title', e.target.value)}
                                placeholder="e.g., ¡Oferta Especial!"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Lead Text (Optional)</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={data.content || ''}
                                onChange={e => handleChange('content', e.target.value)}
                                placeholder="Texto llamativo antes del botón..."
                            />
                        </Form.Group>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Button Text</Form.Label>
                                    <Form.Control type="text" value={data.buttonText || ''} onChange={e => handleChange('buttonText', e.target.value)} />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>URL</Form.Label>
                                    <Form.Control type="text" value={data.buttonUrl || ''} onChange={e => handleChange('buttonUrl', e.target.value)} />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Text Color</Form.Label>
                                    <Form.Control type="color" value={data.color || '#ffffff'} onChange={e => handleChange('color', e.target.value)} />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Button Color</Form.Label>
                                    <Form.Control type="color" value={data.buttonColor || '#007bff'} onChange={e => handleChange('buttonColor', e.target.value)} title="Choose button color" />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Button Width</Form.Label>
                                    <Form.Control type="text" value={data.buttonWidth || ''} onChange={e => handleChange('buttonWidth', e.target.value)} placeholder="e.g., 200px, 100%, auto" />
                                    <Form.Text className="text-muted">Dejar vacío para ancho automático basado en el texto</Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Border Radius</Form.Label>
                                    <Form.Control type="text" value={data.borderRadius || '5px'} onChange={e => handleChange('borderRadius', e.target.value)} placeholder="e.g., 5px, 20px" />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Font Size</Form.Label>
                                    <Form.Control type="text" value={data.fontSize || '16px'} onChange={e => handleChange('fontSize', e.target.value)} placeholder="e.g., 16px, 18px" />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Button Placement (when centered)</Form.Label>
                                    <div>
                                        <Form.Check
                                            inline
                                            label="Above Text"
                                            type="radio"
                                            id="placement-above"
                                            checked={data.buttonPlacement === 'above'}
                                            onChange={() => handleChange('buttonPlacement', 'above')}
                                        />
                                        <Form.Check
                                            inline
                                            label="Below Text"
                                            type="radio"
                                            id="placement-below"
                                            checked={(data.buttonPlacement || 'below') === 'below'}
                                            onChange={() => handleChange('buttonPlacement', 'below')}
                                        />
                                    </div>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Padding Vertical</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={data.buttonPaddingVertical || '12px'}
                                        onChange={e => handleChange('buttonPaddingVertical', e.target.value)}
                                        placeholder="e.g., 12px"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Padding Horizontal</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={data.buttonPaddingHorizontal || '25px'}
                                        onChange={e => handleChange('buttonPaddingHorizontal', e.target.value)}
                                        placeholder="e.g., 25px"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </>
                )

            case 'image':
                return (
                    <>
                        <Form.Group className="mb-3">
                            <Form.Label>Image Source</Form.Label>
                            <div className="d-flex gap-2">
                                <Form.Control type="text" value={data.imageUrl || ''} onChange={e => handleChange('imageUrl', e.target.value)} placeholder="https://..." />
                                <Button variant="outline-secondary" onClick={() => openMediaFor('imageUrl')}>Select</Button>
                            </div>
                            <Form.Text>Choose from Media Manager or paste URL</Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Alt Text (for accessibility)</Form.Label>
                            <Form.Control
                                type="text"
                                value={data.imageAlt || ''}
                                onChange={e => handleChange('imageAlt', e.target.value)}
                                placeholder="Describe the image..."
                            />
                        </Form.Group>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Width</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={data.imageWidth || ''}
                                        onChange={e => handleChange('imageWidth', e.target.value)}
                                        placeholder="e.g., 300px, 100%, auto"
                                    />
                                    <Form.Text>Leave empty for default (100%)</Form.Text>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Height</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={data.imageHeight || ''}
                                        onChange={e => handleChange('imageHeight', e.target.value)}
                                        placeholder="e.g., 200px, auto"
                                    />
                                    <Form.Text>Leave empty for auto</Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Border Radius</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={data.borderRadius || '0px'}
                                        onChange={e => handleChange('borderRadius', e.target.value)}
                                        placeholder="e.g., 8px, 50%"
                                    />
                                    <Form.Text>Use 50% for circular images</Form.Text>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Alignment</Form.Label>
                                    <Form.Select value={data.align || 'center'} onChange={e => handleChange('align', e.target.value)}>
                                        <option value="left">Left</option>
                                        <option value="center">Center</option>
                                        <option value="right">Right</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                    </>
                )

            case '1_col':
            case '2_cols':
            case '3_cols':
                return (
                    <>
                        <Form.Group className="mb-3">
                            <Form.Label>Background Color</Form.Label>
                            <div className="d-flex align-items-center gap-2">
                                <Form.Control
                                    type="color"
                                    value={data.backgroundColor || '#ffffff'}
                                    onChange={e => handleChange('backgroundColor', e.target.value)}
                                    title="Choose color"
                                    className="form-control-color"
                                />
                                <Form.Control
                                    type="text"
                                    value={data.backgroundColor || '#ffffff'}
                                    onChange={e => handleChange('backgroundColor', e.target.value)}
                                />
                            </div>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Padding (e.g., 20px 0)</Form.Label>
                            <Form.Control
                                type="text"
                                value={data.padding || '20px 0'}
                                onChange={e => handleChange('padding', e.target.value)}
                                placeholder="10px 10px 10px 10px"
                            />
                        </Form.Group>

                        {/* Block Spacing */}
                        <Form.Group className="mb-3">
                            <Form.Label>Espaciado Inferior del Bloque</Form.Label>
                            <Form.Control
                                type="text"
                                value={data.blockSpacing || '20px'}
                                onChange={e => handleChange('blockSpacing', e.target.value)}
                                placeholder="20px"
                            />
                            <Form.Text className="text-muted">
                                Espacio después de este bloque. Usa "0px" para pegar bloques.
                            </Form.Text>
                        </Form.Group>

                        {/* Border */}
                        <Form.Group className="mb-3">
                            <Form.Label>Borde del Bloque</Form.Label>
                            <Form.Control
                                type="text"
                                value={data.border || ''}
                                onChange={e => handleChange('border', e.target.value)}
                                placeholder="1px solid #dddddd"
                            />
                            <Form.Text className="text-muted">
                                Ejemplos: "1px solid #ddd", "2px dashed #000", "" (sin borde)
                            </Form.Text>
                        </Form.Group>

                        {/* Column Width Configuration */}
                        {block.id === '2_cols' && (
                            <>
                                <hr className="my-3" />
                                <h6 className="mb-3">📐 Ancho de Columnas</h6>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Columna 1 (Ancho)</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={data.col1Width || '450px'}
                                                onChange={e => handleChange('col1Width', e.target.value)}
                                                placeholder="450px"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Columna 2 (Ancho)</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={data.col2Width || '450px'}
                                                onChange={e => handleChange('col2Width', e.target.value)}
                                                placeholder="450px"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <hr className="my-3" />
                                <h6 className="mb-3">🎨 Estilo por Columna</h6>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Col 1 - Background</Form.Label>
                                            <div className="d-flex gap-2">
                                                <Form.Control
                                                    type="color"
                                                    value={data.col1BackgroundColor || '#ffffff'}
                                                    onChange={e => handleChange('col1BackgroundColor', e.target.value)}
                                                    style={{ width: '60px' }}
                                                />
                                                <Form.Control
                                                    type="text"
                                                    value={data.col1BackgroundColor || '#ffffff'}
                                                    onChange={e => handleChange('col1BackgroundColor', e.target.value)}
                                                    placeholder="#ffffff"
                                                />
                                            </div>
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Col 1 - Padding</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={data.col1Padding || '30px'}
                                                onChange={e => handleChange('col1Padding', e.target.value)}
                                                placeholder="30px"
                                            />
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Col 1 - Borde</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={data.col1Border || ''}
                                                onChange={e => handleChange('col1Border', e.target.value)}
                                                placeholder="1px solid #ddd"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Col 2 - Background</Form.Label>
                                            <div className="d-flex gap-2">
                                                <Form.Control
                                                    type="color"
                                                    value={data.col2BackgroundColor || '#ffffff'}
                                                    onChange={e => handleChange('col2BackgroundColor', e.target.value)}
                                                    style={{ width: '60px' }}
                                                />
                                                <Form.Control
                                                    type="text"
                                                    value={data.col2BackgroundColor || '#ffffff'}
                                                    onChange={e => handleChange('col2BackgroundColor', e.target.value)}
                                                    placeholder="#ffffff"
                                                />
                                            </div>
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Col 2 - Padding</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={data.col2Padding || '30px'}
                                                onChange={e => handleChange('col2Padding', e.target.value)}
                                                placeholder="30px"
                                            />
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Col 2 - Borde</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={data.col2Border || ''}
                                                onChange={e => handleChange('col2Border', e.target.value)}
                                                placeholder="1px solid #ddd"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <div className="alert alert-info small mb-0">
                                    <strong>💡 Tip:</strong> Usa diferentes colores de fondo para crear diseños visuales interesantes (ej: gris + blanco)
                                </div>
                            </>
                        )}

                        {block.id === '3_cols' && (
                            <>
                                <hr className="my-3" />
                                <h6 className="mb-3">📐 Ancho de Columnas</h6>
                                <Row>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Col 1</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={data.col1Width || '300px'}
                                                onChange={e => handleChange('col1Width', e.target.value)}
                                                placeholder="300px"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Col 2</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={data.col2Width || '300px'}
                                                onChange={e => handleChange('col2Width', e.target.value)}
                                                placeholder="300px"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Col 3</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={data.col3Width || '300px'}
                                                onChange={e => handleChange('col3Width', e.target.value)}
                                                placeholder="300px"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <hr className="my-3" />
                                <h6 className="mb-3">🎨 Estilo por Columna</h6>
                                <Row>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="small">Col 1 - BG</Form.Label>
                                            <div className="d-flex gap-1">
                                                <Form.Control
                                                    type="color"
                                                    value={data.col1BackgroundColor || '#ffffff'}
                                                    onChange={e => handleChange('col1BackgroundColor', e.target.value)}
                                                    style={{ width: '50px' }}
                                                />
                                                <Form.Control
                                                    type="text"
                                                    value={data.col1BackgroundColor || '#ffffff'}
                                                    onChange={e => handleChange('col1BackgroundColor', e.target.value)}
                                                    placeholder="#fff"
                                                    size="sm"
                                                />
                                            </div>
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="small">Padding</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={data.col1Padding || '10px'}
                                                onChange={e => handleChange('col1Padding', e.target.value)}
                                                placeholder="10px"
                                                size="sm"
                                            />
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="small">Borde</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={data.col1Border || ''}
                                                onChange={e => handleChange('col1Border', e.target.value)}
                                                placeholder="1px solid #ddd"
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="small">Col 2 - BG</Form.Label>
                                            <div className="d-flex gap-1">
                                                <Form.Control
                                                    type="color"
                                                    value={data.col2BackgroundColor || '#ffffff'}
                                                    onChange={e => handleChange('col2BackgroundColor', e.target.value)}
                                                    style={{ width: '50px' }}
                                                />
                                                <Form.Control
                                                    type="text"
                                                    value={data.col2BackgroundColor || '#ffffff'}
                                                    onChange={e => handleChange('col2BackgroundColor', e.target.value)}
                                                    placeholder="#fff"
                                                    size="sm"
                                                />
                                            </div>
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="small">Padding</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={data.col2Padding || '10px'}
                                                onChange={e => handleChange('col2Padding', e.target.value)}
                                                placeholder="10px"
                                                size="sm"
                                            />
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="small">Borde</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={data.col2Border || ''}
                                                onChange={e => handleChange('col2Border', e.target.value)}
                                                placeholder="1px solid #ddd"
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="small">Col 3 - BG</Form.Label>
                                            <div className="d-flex gap-1">
                                                <Form.Control
                                                    type="color"
                                                    value={data.col3BackgroundColor || '#ffffff'}
                                                    onChange={e => handleChange('col3BackgroundColor', e.target.value)}
                                                    style={{ width: '50px' }}
                                                />
                                                <Form.Control
                                                    type="text"
                                                    value={data.col3BackgroundColor || '#ffffff'}
                                                    onChange={e => handleChange('col3BackgroundColor', e.target.value)}
                                                    placeholder="#fff"
                                                    size="sm"
                                                />
                                            </div>
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="small">Padding</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={data.col3Padding || '10px'}
                                                onChange={e => handleChange('col3Padding', e.target.value)}
                                                placeholder="10px"
                                                size="sm"
                                            />
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="small">Borde</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={data.col3Border || ''}
                                                onChange={e => handleChange('col3Border', e.target.value)}
                                                placeholder="1px solid #ddd"
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <div className="alert alert-info small mb-0">
                                    <strong>💡 Ejemplos:</strong> 450px + 225px + 225px (destacada), 300px + 300px + 300px (igual)
                                </div>
                            </>
                        )}
                    </>
                )

            case 'header_simple':
                return (
                    <>
                        <Form.Group className="mb-3">
                            <Form.Label>Logo Image</Form.Label>
                            <div className="d-flex gap-2">
                                <Form.Control type="text" value={data.logoUrl || ''} onChange={e => handleChange('logoUrl', e.target.value)} placeholder="https://..." />
                                <Button variant="outline-secondary" onClick={() => openMediaFor('logoUrl')}>Select</Button>
                            </div>
                        </Form.Group>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Logo Width</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={data.logoWidth || '150px'}
                                        onChange={e => handleChange('logoWidth', e.target.value)}
                                        placeholder="e.g., 150px, 200px"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Logo Height</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={data.logoHeight || 'auto'}
                                        onChange={e => handleChange('logoHeight', e.target.value)}
                                        placeholder="e.g., auto, 60px"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Form.Group className="mb-3">
                            <Form.Label>Background Color</Form.Label>
                            <Form.Control
                                type="color"
                                value={data.backgroundColor || '#ffffff'}
                                onChange={e => handleChange('backgroundColor', e.target.value)}
                            />
                        </Form.Group>
                    </>
                )

            case 'social_links':
                return (
                    <>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Facebook URL</Form.Label>
                                    <Form.Control type="text" value={data.socialFb || ''} onChange={e => handleChange('socialFb', e.target.value)} placeholder="https://facebook.com/..." />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Instagram URL</Form.Label>
                                    <Form.Control type="text" value={data.socialIg || ''} onChange={e => handleChange('socialIg', e.target.value)} placeholder="https://instagram.com/..." />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>LinkedIn URL</Form.Label>
                                    <Form.Control type="text" value={data.socialIn || ''} onChange={e => handleChange('socialIn', e.target.value)} placeholder="https://linkedin.com/..." />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>X (Twitter) URL</Form.Label>
                                    <Form.Control type="text" value={data.socialX || ''} onChange={e => handleChange('socialX', e.target.value)} placeholder="https://x.com/..." />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>YouTube URL</Form.Label>
                                    <Form.Control type="text" value={data.socialYt || ''} onChange={e => handleChange('socialYt', e.target.value)} placeholder="https://youtube.com/..." />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>WhatsApp URL</Form.Label>
                                    <Form.Control type="text" value={data.socialWa || ''} onChange={e => handleChange('socialWa', e.target.value)} placeholder="https://wa.me/..." />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Icon Size</Form.Label>
                                    <Form.Control type="text" value={data.socialSize || '32px'} onChange={e => handleChange('socialSize', e.target.value)} placeholder="e.g., 24px, 32px" />
                                </Form.Group>
                            </Col>
                        </Row>
                    </>
                )

            case 'footer_copyright':
                return (
                    <>
                        <Form.Group className="mb-3">
                            <Form.Label>Copyright Text</Form.Label>
                            <Form.Control
                                type="text"
                                value={data.copyrightText || `© ${new Date().getFullYear()} Todos los derechos reservados.`}
                                onChange={e => handleChange('copyrightText', e.target.value)}
                                placeholder="© 2026 Tu Empresa. Todos los derechos reservados."
                            />
                            <Form.Text className="text-muted">
                                Texto que aparecerá como copyright
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Unsubscribe Link Text</Form.Label>
                            <Form.Control
                                type="text"
                                value={data.unsubscribeText || 'Cancelar suscripción'}
                                onChange={e => handleChange('unsubscribeText', e.target.value)}
                                placeholder="Cancelar suscripción"
                            />
                            <Form.Text className="text-muted">
                                El link usará automáticamente la variable {'{'}{'{'} unsubscribe_url {'}'}{'}'}
                            </Form.Text>
                        </Form.Group>

                    </>
                )

            case 'spacer':
                return (
                    <Form.Group className="mb-3">
                        <Form.Label>Spacer Height</Form.Label>
                        <Form.Control
                            type="text"
                            value={data.spacerHeight || '40px'}
                            onChange={e => handleChange('spacerHeight', e.target.value)}
                            placeholder="e.g., 40px, 60px, 100px"
                        />
                        <Form.Text className="text-muted">
                            Specify the vertical space in pixels (default: 40px)
                        </Form.Text>
                    </Form.Group>
                )

            default:
                return null
        }
    }

    const renderStyleFields = () => (
        <>
            <Row>
                <Col md={12}>
                    <Form.Group className="mb-3">
                        <Form.Label>Alineación del Bloque</Form.Label>
                        <div className="d-flex gap-3">
                            {['left', 'center', 'right'].map(align => (
                                <Form.Check
                                    key={align}
                                    type="radio"
                                    id={`style-align-${align}`}
                                    label={align.charAt(0).toUpperCase() + align.slice(1)}
                                    checked={data.align === align}
                                    onChange={() => handleChange('align', align)}
                                />
                            ))}
                        </div>
                    </Form.Group>
                </Col>
            </Row>

            <Form.Group className="mb-3">
                <Form.Label>Color de Fondo del Bloque</Form.Label>
                <div className="d-flex gap-2 align-items-center">
                    <Form.Control
                        type="color"
                        value={data.backgroundColor || '#ffffff'}
                        onChange={e => handleChange('backgroundColor', e.target.value)}
                        title="Seleccionar color de fondo"
                        style={{ width: '50px' }}
                    />
                    <Button variant="outline-secondary" size="sm" onClick={() => handleChange('backgroundColor', 'transparent')}>Transparente</Button>
                </div>
            </Form.Group>

            <hr />
            <h6>Espaciado (Padding)</h6>
            <Row>
                <Col md={6}>
                    <Form.Group className="mb-2">
                        <Form.Label className="small">Arriba (Top)</Form.Label>
                        <Form.Control size="sm" type="text" value={data.paddingTop || '20px'} onChange={e => handleChange('paddingTop', e.target.value)} />
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-2">
                        <Form.Label className="small">Abajo (Bottom)</Form.Label>
                        <Form.Control size="sm" type="text" value={data.paddingBottom || '20px'} onChange={e => handleChange('paddingBottom', e.target.value)} />
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Col md={6}>
                    <Form.Group className="mb-2">
                        <Form.Label className="small">Izquierda (Left)</Form.Label>
                        <Form.Control size="sm" type="text" value={data.paddingLeft || '20px'} onChange={e => handleChange('paddingLeft', e.target.value)} />
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-2">
                        <Form.Label className="small">Derecha (Right)</Form.Label>
                        <Form.Control size="sm" type="text" value={data.paddingRight || '20px'} onChange={e => handleChange('paddingRight', e.target.value)} />
                    </Form.Group>
                </Col>
            </Row>

            <hr />
            <h6>Bordes</h6>
            <Row>
                <Col md={6}>
                    <Form.Group className="mb-2">
                        <Form.Label className="small">Grosor</Form.Label>
                        <Form.Control size="sm" type="text" value={data.borderWidth || '0px'} onChange={e => handleChange('borderWidth', e.target.value)} placeholder="0px" />
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-2">
                        <Form.Label className="small">Estilo</Form.Label>
                        <Form.Select size="sm" value={data.borderStyle || 'solid'} onChange={e => handleChange('borderStyle', e.target.value)}>
                            <option value="solid">Sólido</option>
                            <option value="dashed">Guiones</option>
                            <option value="dotted">Puntos</option>
                        </Form.Select>
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Col md={6}>
                    <Form.Group className="mb-2">
                        <Form.Label className="small">Color del Borde</Form.Label>
                        <Form.Control size="sm" type="color" value={data.borderColor || '#dddddd'} onChange={e => handleChange('borderColor', e.target.value)} />
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-2">
                        <Form.Label className="small">Redondeado (Radius)</Form.Label>
                        <Form.Control size="sm" type="text" value={data.borderRadius || '0px'} onChange={e => handleChange('borderRadius', e.target.value)} placeholder="0px" />
                    </Form.Group>
                </Col>
            </Row>
        </>
    )

    return (
        <>
            <Modal show={show} onHide={onHide} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Configurar {block?.label}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Tabs
                        activeKey={activeTab}
                        onSelect={(k) => setActiveTab(k || 'content')}
                        className="mb-3"
                    >
                        <Tab eventKey="content" title="Contenido">
                            <div className="py-2">
                                <Form>
                                    {renderFields()}
                                </Form>
                            </div>
                        </Tab>
                        <Tab eventKey="style" title="Estilo y Diseño">
                            <div className="py-2">
                                <Form>
                                    {renderStyleFields()}
                                </Form>
                            </div>
                        </Tab>
                    </Tabs>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide}>Cancelar</Button>
                    <Button variant="primary" onClick={() => onConfirm(data)}>Guardar Cambios</Button>
                </Modal.Footer>
            </Modal>

            {showMediaManager && (
                <MediaManagerModal
                    isOpen={showMediaManager}
                    onClose={() => setShowMediaManager(false)}
                    onSelect={handleMediaSelect}
                />
            )}
        </>
    )
}
